import { isNative } from '@unifyapps/ui/utils/isNative';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ClientType, useClientContext } from '../../../context/client';
import {
  DevicePerformanceLevel,
  getDevicePerformanceLevel,
} from '../../../utils/deviceCapabilities';
import { deviceCapabilitiesConfig } from '../../../utils/deviceCapabilities/config';
import { useIsLowEndDeviceFromLocalStorage } from '../../hooks/local-storage/useIsLowEndDeviceFromLocalStorage';

export interface DeviceCapabilitiesContextValue {
  deviceLevel: DevicePerformanceLevel;
  override: boolean | null;
  setOverride: (value: boolean | null) => void;
  shouldAnimate: boolean;
}

const DeviceCapabilitiesContext = createContext<DeviceCapabilitiesContextValue | null>(null);

export interface DeviceCapabilitiesProviderProps {
  children: React.ReactNode;
  /** Manual override: true=enable, false=disable, null=auto (default) */
  override?: boolean | null;
}

/**
 * Detects device capabilities and provides animation control flag.
 * Caches result in localStorage. Only runs detection for extension-only contexts.
 * URL params: ?disableAnimations=true | ?forceAnimations=true
 */
export function DeviceCapabilitiesProvider({
  children,
  override: initialOverride = null,
}: DeviceCapabilitiesProviderProps) {
  const clientContext = useClientContext();
  const isExtensionOnly = clientContext?.client === ClientType.MatrixExtension;
  const { value: isLowEndDeviceFromStorage, setValue: setIsLowEndDevice } =
    useIsLowEndDeviceFromLocalStorage();

  // URL params take highest priority
  const urlOverride = useMemo(() => {
    if (isNative() || typeof window === 'undefined') {
      return null;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('disableAnimations') === 'true') {
      return false;
    }
    if (params.get('forceAnimations') === 'true') {
      return true;
    }
    return null;
  }, []);

  const hasInitializedFromStorage = useRef(false);

  // Initialize from localStorage or default to HIGH (non-extension contexts always HIGH)
  const [deviceLevel, setDeviceLevel] = useState<DevicePerformanceLevel>(() => {
    if (!isExtensionOnly || urlOverride !== null) {
      return DevicePerformanceLevel.HIGH;
    }
    // For initial state, default to HIGH, will be updated by effect if value exists
    return DevicePerformanceLevel.HIGH;
  });

  // Sync deviceLevel with localStorage value when it becomes available
  useLayoutEffect(() => {
    if (!isExtensionOnly || urlOverride !== null) {
      return;
    }
    if (isLowEndDeviceFromStorage !== undefined && !hasInitializedFromStorage.current) {
      hasInitializedFromStorage.current = true;
      setDeviceLevel(
        isLowEndDeviceFromStorage ? DevicePerformanceLevel.LOW : DevicePerformanceLevel.HIGH,
      );
    }
  }, [isExtensionOnly, urlOverride, isLowEndDeviceFromStorage]);

  // Run detection only for extension-only contexts when no cached value exists
  useLayoutEffect(() => {
    if (!isExtensionOnly || urlOverride !== null || hasInitializedFromStorage.current) {
      return;
    }

    let cancelled = false;

    const detectCapabilities = async () => {
      try {
        const detectedLevel = await getDevicePerformanceLevel();
        const isLowEnd = detectedLevel === DevicePerformanceLevel.LOW;
        setIsLowEndDevice(isLowEnd);

        if (!cancelled) {
          setDeviceLevel(detectedLevel);
        }
      } catch {
        if (!cancelled) {
          setDeviceLevel(DevicePerformanceLevel.LOW);
          setIsLowEndDevice(true);
        }
      }
    };

    if (typeof window !== 'undefined') {
      detectCapabilities();
    }

    return () => {
      cancelled = true;
    };
  }, [urlOverride, isExtensionOnly, setIsLowEndDevice]);

  const [override, setOverrideState] = useState<boolean | null>(
    urlOverride !== null ? urlOverride : initialOverride,
  );

  const setOverride = useCallback((value: boolean | null) => {
    setOverrideState(value);
  }, []);

  const shouldAnimate = useMemo(() => {
    if (override !== null) {
      return override;
    }
    return deviceLevel === DevicePerformanceLevel.LOW
      ? deviceCapabilitiesConfig.enableAnimationsForLowEnd
      : deviceLevel === DevicePerformanceLevel.MEDIUM ||
          deviceLevel === DevicePerformanceLevel.HIGH;
  }, [deviceLevel, override]);

  const value = useMemo<DeviceCapabilitiesContextValue>(
    () => ({
      deviceLevel,
      override,
      setOverride,
      shouldAnimate,
    }),
    [deviceLevel, override, setOverride, shouldAnimate],
  );

  return (
    <DeviceCapabilitiesContext.Provider value={value}>
      {children}
    </DeviceCapabilitiesContext.Provider>
  );
}

/** Hook to access device capabilities context */
export function useDeviceCapabilities(): DeviceCapabilitiesContextValue {
  const context = useContext(DeviceCapabilitiesContext);

  if (!context) {
    return {
      deviceLevel: DevicePerformanceLevel.LOW,
      override: null,
      setOverride: () => {
        // no-op
      },
      shouldAnimate: deviceCapabilitiesConfig.enableAnimationsForLowEnd,
    };
  }

  return context;
}

/**
 * Whether animations should run — for animations driven from JS rather than CSS.
 *
 * Prefer this over `useDeviceCapabilities().shouldAnimate`. With no provider mounted, that hook
 * falls back to LOW / no-animation. That is the safe default for asking about the *device*, but
 * the wrong one for asking about animations: the CSS kill-switch only fires beneath an explicit
 * `[data-animations-enabled='false']` ancestor, and that attribute is written on interface pages
 * alone. So on any surface without the provider — the platform copilot, the agent playground —
 * every CSS animation happily runs while `shouldAnimate` reports false. A JS-driven animation
 * that trusted that flag would be the one frozen thing on an otherwise animating page.
 *
 * This matches what the CSS does: animate unless a provider explicitly says not to.
 */
export function useShouldAnimate(): boolean {
  const context = useContext(DeviceCapabilitiesContext);

  return context ? context.shouldAnimate : true;
}
